import React, { ClipboardEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Paper, Typography } from '@material-ui/core'
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded'
import { formatOtpCountdown } from './formatOtpCountdown'
import './otpVerificationCard.css'

const OTP_LENGTH = 6

type OtpVerificationCardProps = {
  email: string
  expiresAt: number
  resendAt: number
  loading: boolean
  expired: boolean
  onVerify: (otp: string) => void
  onResend: () => void
  onChangeCredentials: () => void
  onExpired: () => void
}

const OtpVerificationCard: React.FC<OtpVerificationCardProps> = ({
  email,
  expiresAt,
  resendAt,
  loading,
  expired,
  onVerify,
  onResend,
  onChangeCredentials,
  onExpired
}) => {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [now, setNow] = useState(Date.now())
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const expiredRef = useRef(false)
  const otp = digits.join('')
  const resendSeconds = Math.max(0, Math.ceil((resendAt - now) / 1000))
  const expiresSeconds = Math.max(0, Math.ceil((expiresAt - now) / 1000))

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    if (expiresAt > 0 && now >= expiresAt && !expiredRef.current) {
      expiredRef.current = true
      setDigits(Array(OTP_LENGTH).fill(''))
      onExpired()
    }
  }, [expiresAt, now, onExpired])

  const focusInput = (index: number) => inputRefs.current[index]?.focus()

  const setCodeFrom = (value: string, startIndex: number) => {
    const numbers = value.replace(/\D/g, '').slice(0, OTP_LENGTH - startIndex)
    if (!numbers) return

    setDigits(current => {
      const next = [...current]
      numbers.split('').forEach((number, offset) => {
        next[startIndex + offset] = number
      })
      return next
    })
    focusInput(Math.min(startIndex + numbers.length, OTP_LENGTH - 1))
  }

  const handleChange = (index: number, value: string) => {
    if (!value) {
      setDigits(current => current.map((digit, digitIndex) => (digitIndex === index ? '' : digit)))
      return
    }
    setCodeFrom(value, index)
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault()
      setDigits(current => current.map((digit, digitIndex) => (digitIndex === index - 1 ? '' : digit)))
      focusInput(index - 1)
    }
    if (event.key === 'ArrowLeft' && index > 0) focusInput(index - 1)
    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) focusInput(index + 1)
  }

  const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    setCodeFrom(event.clipboardData.getData('text'), index)
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (otp.length === OTP_LENGTH && !loading) onVerify(otp)
  }

  return (
    <Paper className="otp-card" elevation={3} component="section" aria-labelledby="otp-title">
      <form onSubmit={handleSubmit}>
        <Button
          type="button"
          size="small"
          disabled={loading}
          onClick={onChangeCredentials}
          startIcon={<ArrowBackRoundedIcon aria-hidden="true" />}
          className="otp-card__back-action"
        >
          Usar otro correo
        </Button>
        <Typography id="otp-title" component="h1" variant="h5" align="center" className="otp-card__title">
          Verifica tu correo
        </Typography>
        <Typography align="center" color="textSecondary">
          Ingresa el código de 6 dígitos que enviamos a
        </Typography>
        <Typography align="center" className="otp-card__email">
          {email}
        </Typography>

        <fieldset className="otp-card__fieldset">
          <legend className="otp-card__legend">Código de verificación</legend>
          <Box className="otp-card__inputs">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={element => {
                  inputRefs.current[index] = element
                }}
                className="otp-card__input"
                aria-label={`Dígito ${index + 1} de ${OTP_LENGTH}`}
                name={`otp-${index + 1}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                spellCheck={false}
                maxLength={index === 0 ? OTP_LENGTH : 1}
                value={digit}
                disabled={loading || expired}
                autoFocus={index === 0}
                onChange={event => handleChange(index, event.target.value)}
                onKeyDown={event => handleKeyDown(index, event)}
                onPaste={event => handlePaste(index, event)}
              />
            ))}
          </Box>
        </fieldset>

        <Typography align="center" variant="body2" color={expired ? 'error' : 'textSecondary'} aria-live="polite">
          {expired ? (
            'El código expiró. Solicita uno nuevo.'
          ) : (
            <>
              El código vence en <strong>{formatOtpCountdown(expiresSeconds)}</strong>
            </>
          )}
        </Typography>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading || expired || otp.length !== OTP_LENGTH}
          className="otp-card__primary-action"
        >
          {loading ? (
            <>
              <CircularProgress size={22} color="inherit" aria-hidden="true" />
              <span className="otp-card__loading-label">Verificando código…</span>
            </>
          ) : (
            'Verificar código'
          )}
        </Button>

        <Button
          type="button"
          fullWidth
          color="primary"
          variant="outlined"
          disabled={loading || (!expired && resendSeconds > 0)}
          onClick={onResend}
          className="otp-card__resend-action"
        >
          {!expired && resendSeconds > 0 ? `Reenviar en ${resendSeconds} s` : 'Reenviar código'}
        </Button>
      </form>
    </Paper>
  )
}

export default OtpVerificationCard
